<!--
  MimioRichEditor — 워드형 본문 저작 에디터 (TipTap).
  v-model 로 content_html(HTML) 바인딩. 이미지 3경로(버튼/드래그/붙여넣기) + YouTube 임베드 + 캡션/크기/배치.
  사용: <MimioRichEditor v-model="item.contentHtml" :upload-fn="uploadImage" />
  렌더는 article21/MimioContent.vue 와 mimio-content.css 를 공유(WYSIWYG).
-->
<template>
  <div class="mimio-rich-editor" v-if="editor">
    <div class="mre-toolbar" @mousedown.prevent>
      <!-- 블록 스타일 -->
      <div class="mre-group">
        <button type="button" class="mre-btn mre-txt" :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
                @click="editor.chain().focus().toggleHeading({ level: 2 }).run()" title="제목">제목</button>
        <button type="button" class="mre-btn mre-txt" :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
                @click="editor.chain().focus().toggleHeading({ level: 3 }).run()" title="소제목">소제목</button>
        <button type="button" class="mre-btn mre-txt" :class="{ 'is-active': editor.isActive('paragraph') }"
                @click="editor.chain().focus().setParagraph().run()" title="본문">본문</button>
      </div>
      <span class="mre-sep"></span>

      <!-- 인라인 서식 -->
      <div class="mre-group">
        <button type="button" class="mre-btn mre-glyph b" :class="{ 'is-active': editor.isActive('bold') }"
                @click="editor.chain().focus().toggleBold().run()" title="굵게 (⌘B)">B</button>
        <button type="button" class="mre-btn mre-glyph i" :class="{ 'is-active': editor.isActive('italic') }"
                @click="editor.chain().focus().toggleItalic().run()" title="기울임 (⌘I)">I</button>
        <button type="button" class="mre-btn mre-glyph u" :class="{ 'is-active': editor.isActive('underline') }"
                @click="editor.chain().focus().toggleUnderline().run()" title="밑줄 (⌘U)">U</button>
        <select class="mre-select" title="글자 크기" :value="editor.getAttributes('textStyle').fontSize || ''" @mousedown.stop @change="onFontSize($event)">
          <option value="">크기</option>
          <option value="14px">작게</option>
          <option value="17px">보통</option>
          <option value="22px">크게</option>
          <option value="30px">아주크게</option>
        </select>
        <label class="mre-color" title="글자 색" @mousedown.stop>
          <span class="mre-color-a">A</span>
          <input type="color" :value="currentColor()" @input="editor.chain().focus().setColor($event.target.value).run()" />
        </label>
        <button type="button" class="mre-btn mre-ico" title="글자색 초기화"
                @mousedown.stop @click="editor.chain().focus().unsetColor().run()">
          <svg viewBox="0 0 24 24">
            <text x="3" y="17" font-size="15" font-weight="700" fill="currentColor" stroke="none">A</text>
            <line x1="3" y1="21" x2="21" y2="4"/>
          </svg>
        </button>
      </div>
      <span class="mre-sep"></span>

      <!-- 정렬 -->
      <div class="mre-group">
        <button type="button" class="mre-btn mre-ico" :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
                @click="editor.chain().focus().setTextAlign('left').run()" title="왼쪽 정렬">
          <svg viewBox="0 0 24 24"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="13" y2="12"/><line x1="4" y1="18" x2="17" y2="18"/></svg>
        </button>
        <button type="button" class="mre-btn mre-ico" :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }"
                @click="editor.chain().focus().setTextAlign('center').run()" title="가운데 정렬">
          <svg viewBox="0 0 24 24"><line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="5" y1="18" x2="19" y2="18"/></svg>
        </button>
        <button type="button" class="mre-btn mre-ico" :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }"
                @click="editor.chain().focus().setTextAlign('right').run()" title="오른쪽 정렬">
          <svg viewBox="0 0 24 24"><line x1="4" y1="6" x2="20" y2="6"/><line x1="11" y1="12" x2="20" y2="12"/><line x1="7" y1="18" x2="20" y2="18"/></svg>
        </button>
      </div>
      <span class="mre-sep"></span>

      <!-- 목록·구분선 -->
      <div class="mre-group">
        <button type="button" class="mre-btn mre-ico" :class="{ 'is-active': editor.isActive('bulletList') }"
                @click="editor.chain().focus().toggleBulletList().run()" title="목록">
          <svg viewBox="0 0 24 24"><line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/><circle cx="4.5" cy="6" r="1.4" class="fill"/><circle cx="4.5" cy="12" r="1.4" class="fill"/><circle cx="4.5" cy="18" r="1.4" class="fill"/></svg>
        </button>
        <button type="button" class="mre-btn mre-ico" @click="editor.chain().focus().setHorizontalRule().run()" title="구분선">
          <svg viewBox="0 0 24 24"><line x1="3" y1="12" x2="21" y2="12"/><line x1="6" y1="7" x2="18" y2="7" class="faint"/><line x1="6" y1="17" x2="18" y2="17" class="faint"/></svg>
        </button>
      </div>
      <span class="mre-sep"></span>

      <!-- 삽입 -->
      <div class="mre-group">
        <button type="button" class="mre-btn mre-ico-label" @click="pickImage" title="이미지 (드래그·붙여넣기도 가능)">
          <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2.5"/><circle cx="8.5" cy="8.5" r="1.6" class="fill"/><path d="M21 15l-5-5L5 21"/></svg>
          <span>이미지</span>
        </button>
        <button type="button" class="mre-btn mre-ico" :class="{ 'is-active': editor.isActive('link') }" @click="toggleLink" title="링크">
          <svg viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/></svg>
        </button>
        <button type="button" class="mre-btn mre-ico-label" :class="{ 'is-active': inlineMode === 'youtube' }" @click="openInline('youtube')" title="YouTube 동영상">
          <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="3.5"/><path d="M10 8.5l5.5 3.5L10 15.5z" class="fill" /></svg>
          <span>동영상</span>
        </button>
      </div>
    </div>

    <!-- 링크/동영상 주소 인라인 입력 -->
    <div class="mre-toolbar mre-inputbar" v-if="inlineMode" @mousedown.stop>
      <span class="mre-figbar-tag">{{ inlineMode === 'youtube' ? 'YouTube 주소' : '링크 주소' }}</span>
      <input ref="urlInput" class="mre-urlinput" v-model="inlineValue"
             :placeholder="inlineMode === 'youtube' ? 'https://youtu.be/… 붙여넣기' : 'https://…'"
             @keydown.enter.prevent="applyInline" @keydown.esc.prevent="cancelInline" />
      <button type="button" class="mre-btn mre-txt mre-accent" @click="applyInline">적용</button>
      <button type="button" class="mre-btn mre-txt" @click="cancelInline">취소</button>
    </div>

    <!-- 이미지 선택 시에만 뜨는 컨텍스트 툴바 -->
    <div class="mre-toolbar mre-figbar" v-if="figureSelected()" @mousedown.prevent>
      <span class="mre-figbar-tag" v-if="curFig().kind === 'youtube'">
        <svg viewBox="0 0 24 24" class="mre-figbar-ico"><rect x="3" y="5" width="18" height="14" rx="3.5"/><path d="M10 8.5l5.5 3.5L10 15.5z" class="fill" /></svg>
        동영상
      </span>
      <span class="mre-figbar-tag" v-else>
        <svg viewBox="0 0 24 24" class="mre-figbar-ico"><rect x="3" y="3" width="18" height="18" rx="2.5"/><circle cx="8.5" cy="8.5" r="1.6" class="fill"/><path d="M21 15l-5-5L5 21"/></svg>
        이미지
      </span>
      <div class="mre-group">
        <button type="button" class="mre-btn mre-txt" :class="{ 'is-active': curFig().width === '28%' }" @click="figAttr({ width: '28%' })" title="작게">S</button>
        <button type="button" class="mre-btn mre-txt" :class="{ 'is-active': curFig().width === '46%' }" @click="figAttr({ width: '46%' })" title="보통">M</button>
        <button type="button" class="mre-btn mre-txt" :class="{ 'is-active': curFig().width === '70%' }" @click="figAttr({ width: '70%' })" title="크게">L</button>
        <button type="button" class="mre-btn mre-txt" :class="{ 'is-active': curFig().width === '100%' }" @click="figAttr({ width: '100%' })" title="전체폭">전체</button>
      </div>
      <span class="mre-sep"></span>
      <div class="mre-group">
        <button type="button" class="mre-btn mre-ico" :class="{ 'is-active': curFig().float === 'left' }" @click="figAttr({ float: 'left' })" title="왼쪽 배치 (글 흐름)">
          <svg viewBox="0 0 24 24"><rect x="3" y="4" width="8" height="8" rx="1.2" class="fill"/><line x1="13" y1="6" x2="21" y2="6"/><line x1="13" y1="10" x2="21" y2="10"/><line x1="3" y1="16" x2="21" y2="16"/><line x1="3" y1="20" x2="21" y2="20"/></svg>
        </button>
        <button type="button" class="mre-btn mre-ico" :class="{ 'is-active': curFig().float === 'none' }" @click="figAttr({ float: 'none' })" title="가운데 배치">
          <svg viewBox="0 0 24 24"><rect x="7" y="4" width="10" height="8" rx="1.2" class="fill"/><line x1="4" y1="16" x2="20" y2="16"/><line x1="4" y1="20" x2="20" y2="20"/></svg>
        </button>
        <button type="button" class="mre-btn mre-ico" :class="{ 'is-active': curFig().float === 'right' }" @click="figAttr({ float: 'right' })" title="오른쪽 배치 (글 흐름)">
          <svg viewBox="0 0 24 24"><rect x="13" y="4" width="8" height="8" rx="1.2" class="fill"/><line x1="3" y1="6" x2="11" y2="6"/><line x1="3" y1="10" x2="11" y2="10"/><line x1="3" y1="16" x2="21" y2="16"/><line x1="3" y1="20" x2="21" y2="20"/></svg>
        </button>
      </div>
    </div>

    <div class="mre-body">
      <editor-content :editor="editor" class="mre-content" />
      <div v-if="uploading" class="mre-uploading">
        <span class="mre-spinner"></span> 이미지 업로드 중…
      </div>
    </div>
    <input ref="fileInput" type="file" accept="image/*" hidden @change="onFilePick" />
  </div>
</template>

<script setup>
import { watch, onBeforeUnmount, ref, nextTick } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import { StarterKit } from '@tiptap/starter-kit';
import { TextStyle, FontSize, Color } from '@tiptap/extension-text-style';
import { TextAlign } from '@tiptap/extension-text-align';
import { Youtube } from '@tiptap/extension-youtube';
import { NodeSelection } from '@tiptap/pm/state';
import { Figure } from './tiptap-figure';

const props = defineProps({
  modelValue: { type: String, default: '' },
  // 이미지 업로드 핸들러: (File) => Promise<string url>. 없으면 dataURL fallback.
  uploadFn: { type: Function, default: null },
});
const emit = defineEmits(['update:modelValue']);
const fileInput = ref(null);
const uploading = ref(false);             // 이미지 업로드 중 표시
const inlineMode = ref(null);             // null | 'link' | 'youtube' — 주소 인라인 입력 모드
const inlineValue = ref('');
const urlInput = ref(null);

// 레거시 평문(태그 없음) → 줄바꿈 보존 HTML 로 변환해 에디터에 로드.
// 빈 문단(\n\n)은 문단 분리, 단일 \n 은 <br>. 이미 HTML 이면 그대로.
// HTML 판별: TipTap 출력은 블록 태그로 시작하므로 '시작이 태그'인지로 검사
// (단순 /</ 는 "x < y", "<a@b.com>" 같은 평문을 HTML 로 오인).
function toEditorHtml(v) {
  const s = v || '';
  if (!s || /^\s*<[a-zA-Z/]/.test(s)) return s;
  const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return s
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

const editor = useEditor({
  content: toEditorHtml(props.modelValue),
  extensions: [
    StarterKit.configure({ link: { openOnClick: false } }),
    TextStyle,
    FontSize,
    Color,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Youtube.configure({ nocookie: true, controls: true, modestBranding: true }),
    Figure,
  ],
  editorProps: {
    attributes: { class: 'mimio-content mimio-editor' },
    handleClickOn(view, pos, node, nodePos, event) {
      if (event.target && event.target.tagName === 'IMG') {
        view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, nodePos)));
        view.focus();
        return true;
      }
      return false;
    },
    handleDrop(view, event) {
      const files = event.dataTransfer && event.dataTransfer.files;
      if (files && files.length && /^image\//.test(files[0].type)) {
        event.preventDefault();
        insertImageFile(files[0]);
        return true;
      }
      return false;
    },
    handlePaste(view, event) {
      const items = event.clipboardData && event.clipboardData.items;
      if (items) {
        for (const it of items) {
          if (it.type && it.type.startsWith('image/')) {
            const file = it.getAsFile();
            if (file) {
              event.preventDefault();
              insertImageFile(file);
              return true;
            }
          }
        }
      }
      return false;
    },
  },
  // 빈 에디터의 getHTML()은 '<p></p>' → '' 로 정규화(불필요한 dirty/빈 태그 저장 방지)
  onUpdate: ({ editor: ed }) => emit('update:modelValue', ed.isEmpty ? '' : ed.getHTML()),
});

watch(
  () => props.modelValue,
  (val) => {
    const next = toEditorHtml(val);
    if (editor.value && editor.value.getHTML() !== next) {
      editor.value.commands.setContent(next, false);
    }
  }
);

onBeforeUnmount(() => editor.value && editor.value.destroy());

// 색상 피커에 현재 커서 색 반영 — input[type=color]는 hex 만 받으므로 rgb()→hex 변환
function currentColor() {
  const c = (editor.value && editor.value.getAttributes('textStyle').color) || '';
  if (/^#[0-9a-fA-F]{6}$/.test(c)) return c;
  const m = c.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (m) return '#' + [1, 2, 3].map((i) => Number(m[i]).toString(16).padStart(2, '0')).join('');
  return '#222222';
}

// ---- 이미지 삽입 (버튼/드래그/붙여넣기 공통) ----
// uploadFn 실패 시 base64 로 조용히 fallback 하지 않음 — 사용자가 성공으로 오해해
// 거대한 base64 가 DB 에 저장되는 걸 막는다. 실패는 알리고 삽입 중단(null).
function uploadOrDataUrl(file) {
  if (props.uploadFn) {
    return Promise.resolve()
      .then(() => props.uploadFn(file))
      .catch((err) => {
        console.error('이미지 업로드 실패:', err);
        alert('이미지 업로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
        return null;
      });
  }
  return readAsDataUrl(file);
}
function readAsDataUrl(file) {
  return new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });
}
async function insertImageFile(file) {
  uploading.value = true;
  try {
    const url = await uploadOrDataUrl(file);
    if (url) editor.value.chain().focus().insertFigure({ src: url, width: '46%', float: 'none' }).run();
  } finally {
    uploading.value = false;
  }
}
function pickImage() {
  fileInput.value && fileInput.value.click();
}
function onFilePick(e) {
  const f = e.target.files && e.target.files[0];
  if (f) insertImageFile(f);
  e.target.value = '';
}

// ---- 링크 / 동영상 주소 인라인 입력 ----
function openInline(mode) {
  inlineMode.value = mode;
  inlineValue.value = mode === 'link' ? (editor.value.getAttributes('link').href || '') : '';
  nextTick(() => urlInput.value && urlInput.value.focus());
}
function applyInline() {
  const v = (inlineValue.value || '').trim();
  if (v) {
    if (inlineMode.value === 'youtube') {
      const embed = youtubeEmbedUrl(v);
      if (embed) {
        // 동영상도 이미지처럼 크기/배치/캡션 편집되도록 figure 로 삽입
        editor.value.chain().focus().insertVideoFigure({ src: embed, width: '70%', float: 'none' }).run();
      } else {
        alert('유효한 YouTube 주소가 아닙니다.');
      }
    } else {
      const href = /^https?:\/\//i.test(v) ? v : `https://${v}`;
      editor.value.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
  }
  cancelInline();
}
function cancelInline() {
  inlineMode.value = null;
  inlineValue.value = '';
}
// YouTube 주소 → 신뢰 임베드 URL(nocookie). 새니타이저가 youtube-nocookie 호스트만 통과시킴.
function youtubeEmbedUrl(raw) {
  const s = (raw || '').trim();
  let id = '';
  let m = s.match(/youtu\.be\/([\w-]{11})/);
  if (m) id = m[1];
  if (!id && (m = s.match(/[?&]v=([\w-]{11})/))) id = m[1];
  if (!id && (m = s.match(/\/embed\/([\w-]{11})/))) id = m[1];
  if (!id && (m = s.match(/\/shorts\/([\w-]{11})/))) id = m[1];
  return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
}
function toggleLink() {
  if (editor.value.isActive('link')) editor.value.chain().focus().unsetLink().run();
  else openInline('link');
}

// ---- 글자 크기 ----
function onFontSize(e) {
  const v = e.target.value;
  if (v) editor.value.chain().focus().setFontSize(v).run();
  else editor.value.chain().focus().unsetFontSize().run();
}

// ---- figure 속성 변경 + 선택 유지 ----
function figurePos() {
  const sel = editor.value.state.selection;
  if (sel.node && sel.node.type.name === 'figure') return sel.from;
  const $f = sel.$from;
  for (let d = $f.depth; d > 0; d--) if ($f.node(d).type.name === 'figure') return $f.before(d);
  return null;
}
function figAttr(attrs) {
  const pos = figurePos();
  if (pos == null) return;
  editor.value.chain().focus().updateAttributes('figure', attrs).setNodeSelection(pos).run();
}
// 컨텍스트 툴바: 이미지(figure)가 선택됐을 때만 노출 / 현재 figure 속성(크기·배치 활성표시용)
function figureSelected() {
  return !!editor.value && figurePos() != null;
}
function curFig() {
  return editor.value ? editor.value.getAttributes('figure') : {};
}
</script>

<!-- 에디터 chrome 은 ProseMirror DOM(비-Vue-컴파일) 대상이라 scoped 불가 → .mimio-editor 네임스페이스 전역 -->
<style>
.mimio-editor.ProseMirror { outline: none; min-height: 320px; }
.mimio-editor figure.fig .img-wrap { position: relative; }
.mimio-editor figure.fig img { cursor: pointer; }
/* 동영상 figure — 16:9 래퍼. 편집 중엔 iframe pointer-events:none 로 재생 차단(=노드 선택 가능). 발행 사이트는 정상 재생. */
.mimio-editor figure.fig-video .video-wrap { position: relative; width: 100%; aspect-ratio: 16 / 9; }
.mimio-editor figure.fig-video .video-wrap iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; pointer-events: none; }
.mimio-editor figure.fig-video .video-wrap { cursor: pointer; }
.mimio-editor figure.fig.ProseMirror-selectednode { outline: 3px solid #FF5C00; outline-offset: 2px; box-shadow: 0 0 0 6px rgba(255, 92, 0, 0.14); }
.mimio-editor figure.fig.ProseMirror-selectednode::after {
  content: "✓ 선택됨 — 모서리 드래그로 크기조절";
  position: absolute; top: 6px; left: 6px; background: #FF5C00; color: #fff;
  font-size: 11px; line-height: 1; padding: 5px 8px; border-radius: 5px; z-index: 4;
}
.mimio-editor .resize-handle {
  position: absolute; right: -7px; bottom: -7px; width: 15px; height: 15px;
  background: #FF5C00; border: 2px solid #fff; border-radius: 3px; cursor: nwse-resize;
  display: none; z-index: 5; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}
.mimio-editor figure.fig.ProseMirror-selectednode .resize-handle { display: block; }
</style>

<style scoped>
.mimio-rich-editor {
  border: 1px solid #E2E2E5; border-radius: 10px; overflow: hidden;
  background: #fff; box-shadow: 0 1px 2px rgba(16, 18, 22, 0.04);
}

/* ---- 툴바 ---- */
.mre-toolbar {
  display: flex; flex-wrap: wrap; align-items: center; gap: 2px;
  padding: 7px 10px; background: #fff; border-bottom: 1px solid #ECECEF;
  position: sticky; top: 0; z-index: 5;
}
.mre-group { display: flex; align-items: center; gap: 2px; }
.mre-sep { width: 1px; height: 20px; background: #E6E6EA; margin: 0 7px; }

/* 공통 버튼 */
.mre-btn {
  display: inline-flex; align-items: center; justify-content: center;
  height: 30px; min-width: 30px; padding: 0 8px; gap: 5px;
  border: 1px solid transparent; border-radius: 7px; background: transparent;
  color: #3C3F44; font-size: 13px; font-weight: 500; line-height: 1; cursor: pointer;
  transition: background-color .12s ease, color .12s ease, border-color .12s ease;
}
.mre-btn:hover { background: #F1F1F3; }
.mre-btn:active { background: #E6E6EA; }
.mre-btn.is-active { background: #FFF0E8; color: #FF5C00; border-color: #FFD9C4; }

.mre-txt { padding: 0 11px; }
.mre-glyph { font-size: 15px; }
.mre-glyph.b { font-weight: 800; font-family: Georgia, 'Times New Roman', serif; }
.mre-glyph.i { font-style: italic; font-family: Georgia, 'Times New Roman', serif; }
.mre-glyph.u { text-decoration: underline; text-underline-offset: 2px; }

/* 아이콘 */
.mre-btn svg {
  width: 18px; height: 18px; fill: none; stroke: currentColor;
  stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; display: block;
}
.mre-btn svg .fill { fill: currentColor; stroke: none; }
.mre-btn svg .faint { opacity: .4; }
.mre-ico-label { padding: 0 11px 0 9px; }

/* 글자 크기 select */
.mre-select {
  height: 30px; padding: 0 26px 0 10px; border: 1px solid #DEDEE2; border-radius: 7px;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%237A7D83' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 9px center;
  color: #3C3F44; font-size: 13px; cursor: pointer; -webkit-appearance: none; appearance: none;
}
.mre-select:hover { border-color: #C7C7CC; }

/* 색상 피커 */
.mre-color {
  display: inline-flex; flex-direction: column; align-items: center; justify-content: center;
  height: 30px; width: 32px; border: 1px solid transparent; border-radius: 7px; cursor: pointer; gap: 1px;
}
.mre-color:hover { background: #F1F1F3; }
.mre-color-a { font-size: 13px; font-weight: 700; line-height: 1; color: #3C3F44; }
.mre-color input[type='color'] {
  width: 18px; height: 5px; padding: 0; border: none; border-radius: 2px;
  background: none; cursor: pointer;
}
.mre-color input[type='color']::-webkit-color-swatch-wrapper { padding: 0; }
.mre-color input[type='color']::-webkit-color-swatch { border: none; border-radius: 2px; }

/* ---- 이미지 컨텍스트 툴바 ---- */
.mre-figbar { background: #FFF8F4; border-bottom: 1px solid #FCE4D5; }
.mre-figbar .mre-sep { background: #F2D4C2; }
.mre-figbar-tag {
  display: inline-flex; align-items: center; gap: 5px;
  margin-right: 4px; padding-right: 9px;
  font-size: 12px; font-weight: 700; color: #E1530A; letter-spacing: .01em;
}
.mre-figbar-ico { width: 15px; height: 15px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.mre-figbar-ico .fill { fill: currentColor; stroke: none; }

/* ---- 링크/동영상 주소 인라인 입력 바 ---- */
.mre-inputbar { gap: 8px; background: #FFF8F4; border-bottom: 1px solid #FCE4D5; }
.mre-urlinput {
  flex: 1; min-width: 180px; max-width: 460px; height: 30px; padding: 0 11px;
  border: 1px solid #DEDEE2; border-radius: 7px; font-size: 13px; color: #3C3F44; background: #fff;
}
.mre-urlinput:focus { outline: none; border-color: #FF8A4C; box-shadow: 0 0 0 3px rgba(255, 92, 0, 0.12); }
.mre-btn.mre-accent { background: #FF5C00; color: #fff; }
.mre-btn.mre-accent:hover { background: #E84F00; }

/* ---- 본문 + 업로드 표시 ---- */
.mre-body { position: relative; }
.mre-uploading {
  position: absolute; top: 12px; right: 14px; display: inline-flex; align-items: center; gap: 8px;
  padding: 7px 12px; background: rgba(32, 33, 36, 0.86); color: #fff; font-size: 12.5px; font-weight: 500;
  border-radius: 8px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18); z-index: 6;
}
.mre-spinner {
  width: 13px; height: 13px; border: 2px solid rgba(255, 255, 255, 0.35); border-top-color: #fff;
  border-radius: 50%; animation: mre-spin 0.7s linear infinite;
}
@keyframes mre-spin { to { transform: rotate(360deg); } }

.mre-content { padding: 18px 20px; }
</style>
